import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth, api } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { canAccess, hasRole, roleBadges, isReadOnly } from "@/lib/roles";
import { MahasiswaStatusGate } from "@/components/MahasiswaStatusGate";
import { Button } from "@/components/ui/button";
import {
  PenLine,
  CalendarOff,
  Radio,
  Crown,
  NotebookPen,
  BadgeCheck,
  LayoutDashboard,
  BookOpenCheck,
  Boxes,
  ArrowUpCircle,
  Library,
  MessageSquareText,
  Users2,
  Wallet,
  BookText,
  Bus,
  UserCog,
  ShieldCheck,
  Settings,
  CreditCard,
  BarChart3,
  Plug,
  GraduationCap,
  LogOut,
  Menu,
  X,
  IdCard,
  BookOpen,
  BookMarked,
  HeartHandshake,
  Bell,
  KeyRound,
  ClipboardCheck, ScanLine, BookOpenText, ClipboardList, Eye, ScrollText, CalendarClock, HandCoins, Archive } from "lucide-react";

function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const readKey = `notif_read_${user.id || user.username}`;
  const [data, setData] = useState({ items: [] });
  const [readIds, setReadIds] = useState(() => { try { return JSON.parse(localStorage.getItem(readKey) || "[]"); } catch { return []; } });
  const [open, setOpen] = useState(false);
  const sources = [
    hasRole(user, "konselor", "siswa") && { endpoint: "/counseling/notifications", target: "/konseling" },
    hasRole(user, "super_admin", "operator") && { endpoint: "/alumni/pending-notifications", target: "/alumni" },
    hasRole(user, "pustakawan", "super_admin", "siswa", "guru", "kepala_sekolah") && { endpoint: "/karya/notifications", target: hasRole(user, "pustakawan", "super_admin") ? "/verifikasi-karya" : "/karya-saya" },
    hasRole(user, "super_admin", "guru", "bendahara", "pustakawan", "operator", "konselor", "wakil_sdm", "kepala_sekolah") && { endpoint: "/cuti/notifications", target: "/cuti-gtk" },
    hasRole(user, "super_admin", "guru", "wakil_kurikulum", "kepala_sekolah", "operator", "piket", "ketua_kelas") && { endpoint: "/jurnal-kelas/notifications", target: user.role === "guru" ? "/laporan-saya" : "/monitor-kelas" },
    hasRole(user, "super_admin", "wakil_sdm", "guru", "bendahara", "pustakawan", "operator", "konselor", "piket") && { endpoint: "/izin/notifications", target: hasRole(user, "wakil_sdm", "super_admin") ? "/verifikasi-izin" : "/izin" },
    hasRole(user, "super_admin", "bendahara", "guru", "pustakawan", "operator", "konselor", "piket") && { endpoint: "/koperasi/notifications", target: hasRole(user, "bendahara", "super_admin") ? "/koperasi" : "/koperasi-saya" },
    hasRole(user, "super_admin", "operator", "wakil_sarpras") && { endpoint: "/aset/notifications", target: "/aset" },
  ].filter(Boolean);
  const enabled = sources.length > 0;
  const target = sources[0]?.target || "/";
  useEffect(() => {
    if (!enabled) return;
    const load = () => Promise.all(sources.map((s) => api.get(s.endpoint).then((r) => (r.data.items || []).map((n) => ({ ...n, target: n.target || s.target }))).catch(() => [])))
      .then((lists) => {
        const items = lists.flat();
        setData({ items });
        setReadIds((prev) => { const ids = new Set(items.map((i) => i.id)); const kept = prev.filter((id) => ids.has(id)); if (kept.length !== prev.length) localStorage.setItem(readKey, JSON.stringify(kept)); return kept; });
      });
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user.role, user.id]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  if (!enabled) return null;
  const unreadCount = data.items.filter((n) => !readIds.includes(n.id)).length;
  const markAllRead = () => setReadIds((prev) => { const merged = Array.from(new Set([...prev, ...data.items.map((i) => i.id)])); localStorage.setItem(readKey, JSON.stringify(merged)); return merged; });
  const toggleBell = () => setOpen((v) => { const next = !v; if (next) markAllRead(); return next; });
  return (
    <div className="relative">
      <button data-testid="notification-bell" onClick={(e) => { e.stopPropagation(); toggleBell(); }} className="relative z-[62] p-2 rounded-md hover:bg-slate-100 transition-colors">
        <Bell className="h-5 w-5 text-slate-600" />
        {unreadCount > 0 && (
          <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unreadCount}</span>
        )}
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="fixed top-16 right-4 sm:right-8 w-80 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-lg shadow-xl z-[61] overflow-hidden" data-testid="notification-dropdown">
            <div className="px-4 py-3 border-b border-slate-100 font-display font-bold text-sm text-[#0F172A]">Notifikasi</div>
            <div className="max-h-80 overflow-y-auto">
              {data.items.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-slate-400">Tidak ada notifikasi baru.</p>
              ) : data.items.map((n) => (
                <button key={n.id} onClick={() => { setOpen(false); navigate(n.target || target); }}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-0" data-testid={`notif-item-${n.id}`}>
                  <p className={`text-sm ${n.tipe === "stok" ? "text-amber-700" : n.tipe === "karya" ? "text-[#002147]" : "text-[#0F172A]"}`}>{n.text}</p>
                  {n.tanggal && <p className="text-xs text-slate-400 mt-0.5">{n.tanggal}</p>}
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "guru", "bendahara", "siswa", "pustakawan", "operator", "konselor", "piket", "mahasiswa"], end: true },
  { to: "/alumni", label: "Student Tracker", icon: GraduationCap, roles: ["super_admin", "guru", "siswa", "pustakawan", "operator", "konselor", "alumni"] },
  { to: "/data-saya", label: "Data Saya", icon: IdCard, roles: ["siswa"] },
  { to: "/kartu-saya", label: "Kartu Digital", icon: CreditCard, roles: ["siswa"] },
  { to: "/presensi-saya", label: "Presensi Saya", icon: ScanLine, roles: ["siswa"] },
  { to: "/kartu-gtk-saya", label: "Kartu Digital GTK", icon: CreditCard, roles: ["super_admin", "guru", "bendahara", "pustakawan", "operator", "konselor"] },
  { to: "/data-mahasiswa", label: "Data Diri", icon: IdCard, roles: ["mahasiswa"] },
  { to: "/kartu-mahasiswa", label: "Kartu Digital", icon: CreditCard, roles: ["mahasiswa"] },
  { to: "/jurnal-mahasiswa", label: "Jurnal Kegiatan", icon: NotebookPen, roles: ["mahasiswa"] },
  { to: "/mahasiswa-kkn", label: "Mahasiswa KKN/PPL", icon: GraduationCap, roles: ["super_admin", "operator", "wakil_kurikulum"] },
  { to: "/izin", label: "Izin Saya", icon: CalendarClock, roles: ["guru"] },
  { to: "/verifikasi-izin", label: "Verifikasi Izin", icon: ClipboardCheck, roles: ["super_admin", "wakil_sdm"] },
  { to: "/tugas-izin", label: "Tugas Izin", icon: ClipboardList, roles: ["super_admin", "wakil_sdm", "wakil_kurikulum", "piket", "kepala_sekolah"] },
  { to: "/presensi-gtk-saya", label: "Presensi Saya", icon: ScanLine, roles: ["super_admin", "guru", "bendahara", "pustakawan", "operator", "konselor"] },
  { to: "/kiosk/presensi", label: "Presensi (Kiosk)", icon: ScanLine, roles: ["piket"] },
  { to: "/konseling", label: "Konseling", icon: HeartHandshake, roles: ["konselor", "siswa"] },
  { to: "/jurnal-konselor", label: "Jurnal Konselor", icon: NotebookPen, roles: ["konselor"] },
  { to: "/lihat-siswa", label: "Buku Induk Siswa", icon: Users2, roles: ["konselor"] },
  { to: "/siswa", label: "Buku Induk Siswa", icon: Users2, roles: ["super_admin", "guru", "bendahara", "operator"] },
  { to: "/gtk", label: "Buku Induk GTK", icon: UserCog, roles: ["super_admin", "operator"] },
  { to: "/kenaikan-kelas", label: "Kenaikan Kelas", icon: ArrowUpCircle, roles: ["super_admin", "operator"] },
  { to: "/presensi", label: "Presensi & Nilai", icon: ClipboardCheck, roles: ["super_admin", "guru"] },
  { to: "/presensi-harian", label: "Presensi Harian", icon: ScanLine, roles: ["super_admin", "operator", "wakil_kurikulum"] },
  { to: "/cuti-gtk", label: "Cuti & Izin GTK", icon: CalendarOff, roles: ["super_admin", "wakil_sdm", "kepala_sekolah", "wakil_kurikulum", "operator"] },
  { to: "/catatan-kelas", label: "Catatan Kelas Saya", icon: MessageSquareText, roles: ["guru"], wali: true },
  { to: "/catatan-kelas", label: "Catatan Kelas", icon: MessageSquareText, roles: ["wakil_kesiswaan"] },
  { to: "/jurnal", label: "Jurnal Mengajar", icon: BookOpenText, roles: ["super_admin", "guru"] },
  { to: "/monitor-kelas", label: "Monitor Kelas", icon: Radio, roles: ["super_admin", "wakil_kurikulum", "kepala_sekolah", "piket"] },
  { to: "/laporan-saya", label: "Laporan Kelas tentang Saya", icon: MessageSquareText, roles: ["guru"] },
  { to: "/jurnal-kelas", label: "Jurnal Kelas", icon: Crown, roles: ["ketua_kelas", "piket"] },
  { to: "/tugas-saya", label: "Tugas & Materi", icon: ClipboardList, roles: ["siswa"] },
  { to: "/akademik", label: "Akademik", icon: BookOpenCheck, roles: ["super_admin", "guru", "operator", "konselor"] },
  { to: "/nilai-saya", label: "Nilai Rapor", icon: BookOpenCheck, roles: ["siswa"] },
  { to: "/catatan-wali-saya", label: "Catatan Wali Kelas", icon: MessageSquareText, roles: ["siswa"] },
  { to: "/kartu", label: "Kartu Siswa", icon: CreditCard, roles: ["super_admin", "guru", "bendahara", "operator"] },
  { to: "/spp", label: "Pembayaran SPP", icon: Wallet, roles: ["super_admin", "bendahara", "siswa"] },
  { to: "/beasiswa", label: "Beasiswa", icon: GraduationCap, roles: ["super_admin", "bendahara"] },
  { to: "/transport", label: "Pembayaran Transport", icon: Bus, roles: ["super_admin", "bendahara", "siswa"] },
  { to: "/peserta-transport", label: "Peserta Transport", icon: Bus, roles: ["super_admin", "bendahara"] },
  { to: "/buku-kas", label: "Buku Kas Umum", icon: BookText, roles: ["super_admin", "bendahara"] },
  { to: "/koperasi", label: "Koperasi GTK", icon: HandCoins, roles: ["super_admin", "bendahara"] },
  { to: "/koperasi-saya", label: "Koperasi Saya", icon: HandCoins, roles: ["guru", "bendahara", "pustakawan", "operator", "konselor", "piket"] },
  { to: "/laporan-transport", label: "Laporan Transport", icon: BarChart3, roles: ["super_admin", "bendahara"] },
  { to: "/peminjaman", label: "Peminjaman Buku", icon: BookMarked, roles: ["super_admin", "pustakawan"] },
  { to: "/buku", label: "Koleksi Buku", icon: BookOpen, roles: ["super_admin", "pustakawan"] },
  { to: "/laporan-perpustakaan", label: "Laporan Perpustakaan", icon: BarChart3, roles: ["super_admin", "pustakawan"] },
  { to: "/kunjungan-perpustakaan", label: "Kunjungan Perpustakaan", icon: Users2, roles: ["super_admin", "pustakawan"] },
  { to: "/katalog-buku", label: "Katalog Buku", icon: Library, roles: ["siswa", "guru", "pustakawan", "super_admin"] },
  { to: "/karya-saya", label: "Karya Saya", icon: PenLine, roles: ["siswa", "guru", "kepala_sekolah", "pustakawan"] },
  { to: "/verifikasi-karya", label: "Verifikasi Karya", icon: BadgeCheck, roles: ["super_admin", "pustakawan"] },
  { to: "/inventaris", label: "Inventaris ATK", icon: Boxes, roles: ["super_admin", "operator"] },
  { to: "/aset", label: "Inventaris Aset", icon: Archive, roles: ["super_admin", "operator", "wakil_sarpras", "kepala_sekolah"] },
  { to: "/laporan", label: "Laporan", icon: BarChart3, roles: ["super_admin", "bendahara"] },
  { to: "/rekap-kelas", label: "Rekap per Kelas", icon: Users2, roles: ["super_admin", "bendahara", "guru"] },
  { to: "/pengguna", label: "Pengguna", icon: UserCog, roles: ["super_admin"] },
  { to: "/log-aktivitas", label: "Log Aktivitas Sistem", icon: ScrollText, roles: ["super_admin"] },
  { to: "/admin", label: "Kelola Admin", icon: ShieldCheck, roles: ["super_admin"] },
  { to: "/integrasi", label: "Integrasi API", icon: Plug, roles: ["super_admin"] },
  { to: "/pengaturan", label: "Pengaturan", icon: Settings, roles: ["super_admin"] },
  { to: "/akun", label: "Akun Saya", icon: KeyRound, roles: ["super_admin", "guru", "bendahara", "siswa", "pustakawan", "operator", "konselor", "alumni", "piket", "mahasiswa"] },
];

const PRIMARY = {
  super_admin: ["/", "/siswa", "/jurnal", "/spp"],
  guru: ["/", "/jurnal", "/presensi", "/catatan-kelas"],
  bendahara: ["/", "/spp", "/buku-kas", "/transport"],
  siswa: ["/", "/tugas-saya", "/spp", "/kartu-saya"],
  pustakawan: ["/", "/peminjaman", "/buku", "/katalog-buku"],
  operator: ["/", "/siswa", "/gtk", "/presensi-harian"],
  konselor: ["/", "/konseling", "/lihat-siswa", "/akademik"],
  piket: ["/", "/kiosk/presensi", "/monitor-kelas", "/jurnal-kelas"],
  alumni: ["/alumni", "/akun"],
  kepala_sekolah: ["/", "/jurnal", "/siswa", "/laporan"],
  wakil_kurikulum: ["/", "/jurnal", "/akademik", "/presensi"],
  wakil_kesiswaan: ["/", "/presensi-harian", "/siswa", "/catatan-kelas"],
  wakil_sdm: ["/", "/gtk", "/siswa", "/jurnal"],
  wakil_keuangan: ["/", "/spp", "/buku-kas", "/transport"],
  wakil_humas: ["/", "/siswa", "/alumni", "/kartu"],
  wakil_sarpras: ["/", "/inventaris", "/buku", "/peminjaman"],
  mahasiswa: ["/", "/data-mahasiswa", "/kartu-mahasiswa", "/jurnal-mahasiswa"],
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navMatch = NAV.find((n) => n.to === "/" + location.pathname.replace(/^\/+/, "").split("/")[0]);
  const readOnly = isReadOnly(user, location.pathname, navMatch?.roles);

  const items = NAV.filter((n) => canAccess(user, n.to, n.roles) && (!n.wali || user.wali_kelas || !hasRole(user, "guru") || hasRole(user, "wakil_kesiswaan")))
    .filter((n, i, arr) => arr.findIndex((x) => x.to === n.to) === i);
  const primary = (PRIMARY[user.role] || []).map((to) => items.find((i) => i.to === to)).filter(Boolean).slice(0, 4);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-6 h-20 [@media(max-height:820px)]:h-14 border-b border-white/10 shrink-0">
        <div className="h-9 w-9 rounded-lg bg-amber-400 flex items-center justify-center shrink-0">
          <GraduationCap className="h-5 w-5 text-[#002147]" strokeWidth={2.4} />
        </div>
        <div className="min-w-0">
          <p className="font-display font-extrabold text-white text-sm leading-none">Sistem Informasi</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Sekolah Indonesia Jeddah</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-6 [@media(max-height:820px)]:py-3 space-y-1 [@media(max-height:820px)]:space-y-0.5 overflow-y-auto min-h-0">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 [@media(max-height:820px)]:py-1.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white text-[#002147]"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 [@media(max-height:820px)]:p-3 border-t border-white/10 shrink-0">
        <div className="flex items-center gap-3 px-2 mb-3 [@media(max-height:820px)]:mb-1">
          <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center text-white text-sm font-bold shrink-0">
            {(user.name || user.username || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{user.name || user.username}</p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-amber-400 font-bold leading-tight" data-testid="sidebar-roles">
              {roleBadges(user).map((r) => ROLE_LABELS[r] || r).join(" · ")}
            </p>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          data-testid="logout-button"
          variant="ghost"
          className="w-full justify-start gap-3 text-white/70 hover:bg-white/10 hover:text-white h-10"
        >
          <LogOut className="h-[18px] w-[18px]" />
          Keluar
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <MahasiswaStatusGate />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-[#002147] fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      <div className={`lg:hidden fixed inset-0 z-40 ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
        <div className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
        <aside className={`absolute inset-y-0 left-0 w-[82vw] max-w-72 bg-[#002147] h-full overflow-hidden shadow-2xl transition-transform duration-250 ease-out ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="mobile-drawer">
          <SidebarContent />
        </aside>
      </div>

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-20 h-14 sm:h-16 flex items-center justify-between px-3 sm:px-8 backdrop-blur-xl bg-white/70 border-b border-slate-200">
          <button
            className="lg:hidden p-2 -ml-1 rounded-md active:bg-slate-100"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold hidden sm:block">
            SISTEM INFORMASI SEKOLAH INDONESIA JEDDAH
          </p>
          <p className="sm:hidden font-display font-extrabold text-[#002147] text-sm tracking-tight">SIS Jeddah</p>
          <div className="flex items-center gap-2 sm:gap-3 text-sm text-slate-600 ml-auto">
            <NotificationBell />
            <span className="hidden sm:inline">Halo,</span>
            <span className="font-semibold text-[#002147] max-w-[110px] sm:max-w-none truncate">{user.name || user.username}</span>
          </div>
        </header>

        <main className="p-4 sm:p-8 max-w-[1400px] pb-24 lg:pb-8" data-readonly={readOnly ? "true" : undefined} data-testid="main-content">
          {readOnly && <div className="mb-4 inline-flex items-center gap-2 text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-3 py-1" data-testid="readonly-badge"><Eye className="h-3.5 w-3.5" /> Mode baca — Anda hanya dapat melihat data di halaman ini</div>}
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        {primary.length > 0 && (
          <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#002147] border-t border-white/10 pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
            <div className="grid" style={{ gridTemplateColumns: `repeat(${primary.length + 1}, minmax(0, 1fr))` }}>
              {primary.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} data-testid={`bottom-nav-${item.to.replace(/\//g, "") || "dashboard"}`}
                  className={({ isActive }) => `flex flex-col items-center gap-1 py-2 text-[10px] font-semibold leading-none transition-colors ${isActive ? "text-amber-400" : "text-white/60 active:text-white"}`}>
                  {({ isActive }) => <><span className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isActive ? "bg-white/10" : ""}`}><item.icon className="h-[18px] w-[18px]" /></span><span className="truncate max-w-full px-1">{item.label.replace("Pembayaran ", "").replace(" Saya", "")}</span></>}
                </NavLink>
              ))}
              <button onClick={() => setOpen(true)} data-testid="bottom-nav-more" className="flex flex-col items-center gap-1 py-2 text-[10px] font-semibold leading-none text-white/60 active:text-white">
                <span className="h-8 w-8 rounded-lg flex items-center justify-center"><Menu className="h-[18px] w-[18px]" /></span><span>Lainnya</span>
              </button>
            </div>
          </nav>
        )}
      </div>
    </div>
  );
}
